import { StanTag } from '@/components';
import {
    PaymentButtonArea,
    PaymentDetailsCard,
    PaymentPlanButtonArea,
    TransactionAdditionalInfo,
    TransactionCashoutDetails,
    TransactionGeneralDetails,
    TransactionPaymentDetails,
    TransactionTimelineDetails,
} from '@/features/income';
import { useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import IcPayment from '@/assets/icons/ic-wallet.svg';
import IcClock from '@/assets/icons/ic-clock.svg';
import IcTransaction from '@/assets/icons/ic-credit-card.svg';
import IcClicking from '@/assets/icons/ic-clicking.svg';
import IcPlusCircle from '@/assets/icons/ic-plus-circle.svg';
import { TransactionTrafficDetails } from '@/features/income/components/TransactionTrafficDetails';
import { PAYMENT_STATUS } from '@/lib/constants';
import { createIconElement } from '@/lib';
import { formatDistanceToNowStrict } from 'date-fns';
import { TransactionPaymentPlanDetails } from '@/features/income/components/TransactionPaymentPlanDetails';
import { DisputeWarning } from '@/features/income/components/DisputeWarning';
import { RetryingPaymentBanner } from '@/features/income/components/RetryingPaymentBanner';
import { GeneralButtonArea } from '@/features/income/components/GeneralButtonArea';
import { useStanRouter } from '@/hooks/useStanRouter';
import { usePaymentDetails } from '@/queries/useIncome';
import { useBreadcrumbs } from '@/hooks/useBreadcrumbs';

export default function PaymentDetailsPage() {
    const { paymentId } = useParams();
    const [searchParams] = useSearchParams();
    const { push } = useStanRouter();

    // Check if navigating from customer context
    const userFanId = searchParams.get('userFanId');
    const customerName = searchParams.get('customerName');

    // Build breadcrumbs based on context
    const breadcrumbs = useMemo(() => {
        if (userFanId && customerName) {
            // Coming from customer details - show customer context
            return [
                { title: 'My Customers', to: '/audience', id: 'customers' },
                { title: customerName, to: `/audience/details/${userFanId}`, id: 'customer-details' },
                { title: 'Transaction', to: `/income/details/${paymentId}`, active: true, id: 'transaction' },
            ];
        }
        // Default income context
        return [
            { title: 'My Income', to: '/income', id: 'income' },
            { title: 'Overview', to: `/income/details/${paymentId}`, active: true, id: 'details' },
        ];
    }, [userFanId, customerName, paymentId]);

    useBreadcrumbs(breadcrumbs);

    const {
        data: paymentData,
        refetch: refetchPaymentDetails,
        isError: isPaymentDetailsError,
    } = usePaymentDetails(Number(paymentId));

    useEffect(() => {
        if (isPaymentDetailsError) {
            push('/income');
        }
    }, [isPaymentDetailsError, push]);

    const showTrafficDetails =
        paymentData?.pos_data.tags?.referrer ||
        paymentData?.pos_data?.tags?.utmSource ||
        paymentData?.pos_data.tags?.utmMedium ||
        paymentData?.pos_data.tags?.utmCampaign;

    const paymentPlanId = paymentData?.data.payment_plan_id;
    const showPaymentPlan = !!paymentPlanId;
    const isPaymentPlanCancellable = paymentData?.payment_plan_status === 1;
    const isAffiliateTransaction = paymentData?.data?.affiliate_share?.is_qualified_affiliate_transaction;
    const showTimelineDetails =
        paymentData?.stripe_events && paymentData?.stripe_events.length > 0 && !isAffiliateTransaction;

    const showAdditionalInfo =
        paymentData?.data?.note ||
        (paymentData?.data?.additional_information && paymentData.data.additional_information.length > 0);

    const isPayout = paymentData?.product_type === 'payout';

    const payoutEstimatedArrivalText = useMemo(() => {
        // Only display estimated arrival if it is in the future
        const estimatedArrivalDate = paymentData?.pos_data?.payout_details?.arrival_date
            ? paymentData.pos_data.payout_details.arrival_date * 1000
            : null;
        const estimatedArrivalDateString =
            estimatedArrivalDate && estimatedArrivalDate > Date.now()
                ? formatDistanceToNowStrict(estimatedArrivalDate, { addSuffix: false })
                : '';
        return estimatedArrivalDateString ? `Estimated arrival ${estimatedArrivalDateString}` : 'Pending';
    }, [paymentData]);

    const goToCustomerDetails = () => {
        if (paymentData?.user_fan_id) {
            push(`/audience/details/${paymentData?.user_fan_id}`);
        }
    };

    const goToProductDetails = () => {
        if (
            paymentData &&
            paymentData.page_id &&
            paymentData.store_id &&
            paymentData.page_type &&
            paymentData.store_type
        ) {
            const storeType = paymentData.store_type === 'linksite' ? 'store' : paymentData.store_type;
            push(`/${storeType}s/${paymentData.store_id}/page/${paymentData.page_type}/${paymentData.page_id}`);
        }
    };

    return (
        <div className='flex w-full flex-col gap-6'>
            <div className='text-xl font-bold'>Orders</div>
            {paymentData && paymentData.data && paymentId && (
                <DisputeWarning paymentData={paymentData} paymentId={paymentId} />
            )}

            <div className='flex flex-col gap-6'>
                {isPayout ? (
                    <PaymentDetailsCard
                        titleArea={
                            <div className='flex grow'>
                                {createIconElement(IcPayment, { className: 'bg-neutral-subtle p-0.5 rounded-sm' })}
                                <span className='ml-3'>Cash Out</span>
                            </div>
                        }
                        contentArea={paymentData && <TransactionCashoutDetails paymentData={paymentData} />}
                        buttonArea={
                            <div className='hidden items-center gap-3 md:flex'>
                                {paymentData?.status === PAYMENT_STATUS.PENDING && (
                                    <StanTag
                                        label={payoutEstimatedArrivalText}
                                        className='bg-neutral-subtle text-info-dark'
                                    />
                                )}
                                {paymentData?.status === PAYMENT_STATUS.CREATOR_FULFILLED && (
                                    <StanTag label='Paid' className='bg-seafoam-subtle text-seafoam-dark' />
                                )}

                                {paymentData?.status === PAYMENT_STATUS.FAILED && (
                                    <StanTag label='Failed' className='bg-negative-soft text-negative-bold' />
                                )}
                            </div>
                        }
                    />
                ) : (
                    <PaymentDetailsCard
                        titleArea={
                            <>
                                <div className='flex grow'>
                                    {createIconElement(IcPayment, { className: 'bg-neutral-subtle p-0.5 rounded-sm' })}
                                    <span className='ml-3'>Payment</span>
                                </div>
                            </>
                        }
                        buttonArea={
                            paymentData && (
                                <PaymentButtonArea
                                    paymentData={paymentData}
                                    onRefreshData={() => refetchPaymentDetails()}
                                />
                            )
                        }
                        contentArea={
                            paymentData && (
                                <>
                                    <TransactionPaymentDetails paymentData={paymentData} />
                                    <RetryingPaymentBanner
                                        paymentData={paymentData}
                                        onRefreshData={() => refetchPaymentDetails()}
                                    />
                                </>
                            )
                        }
                    />
                )}

                {!isPayout && (
                    <PaymentDetailsCard
                        titleArea={
                            <>
                                {createIconElement(IcTransaction, { className: 'bg-neutral-subtle p-0.5 rounded-sm' })}
                                <div className='ml-3'>Transaction</div>
                            </>
                        }
                        buttonArea={
                            paymentData && paymentId && paymentData.product_type !== 'affiliate-fee' ? (
                                <GeneralButtonArea paymentData={paymentData} paymentId={paymentId} />
                            ) : undefined
                        }
                        contentArea={
                            paymentData && (
                                <TransactionGeneralDetails
                                    paymentData={paymentData}
                                    onCustomerClick={goToCustomerDetails}
                                    onProductClick={goToProductDetails}
                                />
                            )
                        }
                    />
                )}
                {showTrafficDetails && (
                    <PaymentDetailsCard
                        titleArea={
                            <>
                                {createIconElement(IcClicking, { className: 'bg-neutral-subtle p-0.5 rounded-sm' })}
                                <span className='ml-3'>Traffic</span>
                            </>
                        }
                        contentArea={paymentData && <TransactionTrafficDetails paymentData={paymentData} />}
                    />
                )}
                {showTimelineDetails && (
                    <PaymentDetailsCard
                        titleArea={
                            <>
                                {createIconElement(IcClock, { className: 'bg-neutral-subtle p-0.5 rounded-sm' })}
                                <span className='ml-3'>Timeline</span>
                            </>
                        }
                        contentArea={paymentData && <TransactionTimelineDetails paymentData={paymentData} />}
                    />
                )}
                {showPaymentPlan && (
                    <PaymentDetailsCard
                        titleArea={
                            <>
                                {createIconElement(IcClock, { className: 'bg-neutral-subtle p-0.5 rounded-sm' })}
                                <span className='ml-3'>Payment Plan</span>
                            </>
                        }
                        contentArea={paymentData && <TransactionPaymentPlanDetails paymentData={paymentData} />}
                        buttonArea={
                            isPaymentPlanCancellable ? (
                                <PaymentPlanButtonArea
                                    paymentPlanId={paymentPlanId}
                                    onPaymentPlanUpdate={() => refetchPaymentDetails()}
                                />
                            ) : undefined
                        }
                    />
                )}
                {showAdditionalInfo && (
                    <PaymentDetailsCard
                        titleArea={
                            <>
                                {createIconElement(IcPlusCircle, { className: 'bg-neutral-subtle p-0.5 rounded-sm' })}
                                <span className='ml-3'>Additional Info.</span>
                            </>
                        }
                        contentArea={paymentData && <TransactionAdditionalInfo paymentData={paymentData} />}
                    />
                )}
            </div>
        </div>
    );
}
